import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { codigoDeLoEscaneado } from './licenciaDeTerminal';
import { esPermisoNegado } from './vinculoEnLinea';

/* =====================================================================
   El escáner del vínculo en línea · DISEÑO-ACTIVACION-EN-LINEA §6.2 (D11)
   =====================================================================
   `html5-qrcode`, que ya viene en el paquete: usa el BarcodeDetector del
   navegador donde existe (Chrome en Android) y su decodificador propio,
   empaquetado y sin CDN, donde no (Safari en iPhone). Solo QR.

   Lo que lee pasa por `codigoDeLoEscaneado` (acepta `…/t/vincular?c=…`,
   `…/T/XXXXXX` y el código suelto). Cualquier otra cosa: aviso amarillo y
   la cámara SIGUE abierta. Con el permiso de cámara negado se llama a
   `onPermisoNegado` y la pantalla pasa sola a «Escribir el código».

   No toca `mobile/EscanerCamara.jsx` (otro carril) y no dibuja ningún QR
   (la regla «un solo archivo dibuja un QR» no se toca).
   ===================================================================== */

export const NO_ES_DE_TEGO = 'Ese QR no es de una terminal TEGO.';
export const NO_ES_DE_TEGO_QUE_HACER = 'Apunta al código que aparece en la pantalla de la terminal.';
const ID_CAJA = 'lector-vinculo';

export default function EscanerDeVinculo({ onCodigo, onPermisoNegado, onCerrar }) {
  const caja = useRef(null);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);
  const [abriendo, setAbriendo] = useState(true);

  useEffect(() => {
    let cancelado = false;
    const scanner = new Html5Qrcode(ID_CAJA, {
      formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
      experimentalFeatures: { useBarCodeDetectorIfSupported: true },
      verbose: false,
    });
    const nodo = caja.current;
    const ancho = typeof window !== 'undefined' && window.innerWidth < 360 ? 220 : 240;
    const arranque = scanner
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: ancho, height: ancho } },
        (texto) => {
          if (cancelado) return;
          const codigo = codigoDeLoEscaneado(texto);
          if (!codigo) { setAviso(NO_ES_DE_TEGO); return; }
          cancelado = true;
          scanner.stop().catch(() => {}).finally(() => onCodigo(codigo));
        },
        () => {},
      )
      .then(() => { if (!cancelado) setAbriendo(false); })
      .catch((err) => {
        setAbriendo(false);
        if (esPermisoNegado(err)) { onPermisoNegado?.(); return; }
        setError('No se pudo abrir la cámara. Escribe el código.');
      });
    // AL CERRAR, LA CÁMARA SE APAGA SIEMPRE (como TC-DR-AT en EscanerCamara).
    return () => {
      cancelado = true;
      const pistas = () => [...(nodo?.querySelectorAll('video') || [])]
        .flatMap((v) => v.srcObject?.getTracks?.() || []);
      const antes = pistas();
      Promise.resolve(arranque)
        .then(() => (scanner.isScanning ? scanner.stop() : undefined))
        .catch(() => {})
        .finally(() => { [...antes, ...pistas()].forEach((t) => t.stop()); });
    };
  }, [onCodigo, onPermisoNegado]);

  return (
    <div className="space-y-2" data-escaner-vinculo="">
      {abriendo && <p role="status" className="text-sm text-gray-700">Abriendo la cámara…</p>}
      <div id={ID_CAJA} ref={caja} className="w-full overflow-hidden rounded-xl bg-black" />
      {aviso && (
        <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
             data-aviso-escaner="no-es-de-tego">
          <strong className="block">{aviso}</strong>
          <span>{NO_ES_DE_TEGO_QUE_HACER}</span>
        </div>
      )}
      {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-900">{error}</p>}
      <button type="button" onClick={onCerrar}
              className="min-h-11 w-full rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-800">
        Cerrar la cámara
      </button>
    </div>
  );
}
